"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";
import { REF_BHAVA_CLASSES_EN, REF_GRAHA_DRISHTI_EN } from "@/lib/reference-tables.generated";

/**
 * Drishti and bhava class come from the GENERATED engine projection, never from
 * a table typed into this file (rule 29). `REF_GRAHA_DRISHTI_EN` is rendered by
 * `execution/_reference_table_graha_datasets.py`, which reads the engine's own
 * `BASE_DRISHTI_OFFSET` / `SPECIAL_DRISHTI_OFFSETS` by AST — so this studio and
 * `dosha_result.aspects_house()` cannot state different aspect sets. The values
 * previously hard-coded here happened to match the engine on the day they were
 * written; nothing would have caught it the day they stopped.
 */
function aspectOffsets(planetName: string): number[] {
  const row = REF_GRAHA_DRISHTI_EN.rows.find((entry) => entry[0] === planetName);
  if (!row) return [];
  return String(row[1])
    .split(",")
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((value) => Number.isFinite(value));
}

/**
 * Kendra/Panapara/Apoklima is a complete partition of the twelve bhavas, so
 * every house resolves to at least one real class. The hand-written table this
 * replaces had no entry for the 2nd and labelled it "Neutral", which is not a
 * Parashari class — a definitional fact rendered as an invention (rule 29).
 */
function bhavaClasses(bhava: number): string[] {
  return REF_BHAVA_CLASSES_EN.rows
    .filter((row) =>
      String(row[1])
        .split(",")
        .map((part) => Number.parseInt(part.trim(), 10))
        .includes(bhava)
    )
    .map((row) => String(row[0]));
}

interface PlanetDef {
  id: string;
  name_en: string;
  vedic: string;
  symbol: string;
  karaka: string;
  element: string;
}

interface HouseDef {
  number: number;
  name: string;
  sanskrit: string;
  domain: string;
}

const PLANETS: PlanetDef[] = [
  {
    id: "sun",
    name_en: "Sun",
    vedic: "Surya",
    symbol: "☀️",
    karaka: "Soul, Authority, Father, Vitality",
    element: "Fire",
  },
  {
    id: "moon",
    name_en: "Moon",
    vedic: "Chandra",
    symbol: "🌙",
    karaka: "Mind, Mother, Emotions, Public Life",
    element: "Water",
  },
  {
    id: "mars",
    name_en: "Mars",
    vedic: "Mangal",
    symbol: "🔴",
    karaka: "Courage, Property, Siblings, Action",
    element: "Fire",
  },
  {
    id: "mercury",
    name_en: "Mercury",
    vedic: "Budh",
    symbol: "🟢",
    karaka: "Intellect, Speech, Commerce, Logic",
    element: "Earth",
  },
  {
    id: "jupiter",
    name_en: "Jupiter",
    vedic: "Guru",
    symbol: "🟡",
    karaka: "Wisdom, Wealth, Children, Dharma",
    element: "Ether",
  },
  {
    id: "venus",
    name_en: "Venus",
    vedic: "Shukra",
    symbol: "⚪",
    karaka: "Love, Marriage, Luxury, Vehicles, Arts",
    element: "Water",
  },
  {
    id: "saturn",
    name_en: "Saturn",
    vedic: "Shani",
    symbol: "🪐",
    karaka: "Karma, Discipline, Career, Longevity",
    element: "Air",
  },
  {
    id: "rahu",
    name_en: "Rahu",
    vedic: "Rahu",
    symbol: "🐉",
    karaka: "Ambition, Foreign Travel, Innovation",
    element: "Air/Smoke",
  },
  {
    id: "ketu",
    name_en: "Ketu",
    vedic: "Ketu",
    symbol: "☄️",
    karaka: "Moksha, Spirituality, Detachment",
    element: "Fire",
  },
];

const HOUSES: HouseDef[] = [
  {
    number: 1,
    name: "1st House",
    sanskrit: "Lagna / Tanu",
    domain: "Self, Personality, Physical Body, Life Path",
  },
  {
    number: 2,
    name: "2nd House",
    sanskrit: "Dhana / Kutumba",
    domain: "Accumulated Wealth, Family, Speech, Food",
  },
  {
    number: 3,
    name: "3rd House",
    sanskrit: "Sahaja / Bhratri",
    domain: "Courage, Siblings, Communication, Short Travel",
  },
  {
    number: 4,
    name: "4th House",
    sanskrit: "Sukha / Matri",
    domain: "Home, Mother, Vehicles, Inner Peace, Property",
  },
  {
    number: 5,
    name: "5th House",
    sanskrit: "Putra / Purva Punya",
    domain: "Children, Intelligence, Creativity, Past Karma",
  },
  {
    number: 6,
    name: "6th House",
    sanskrit: "Shatru / Roga",
    domain: "Overcoming Enemies, Health, Daily Service, Debt",
  },
  {
    number: 7,
    name: "7th House",
    sanskrit: "Kalatra / Jaya",
    domain: "Marriage, Life Partner, Business Partnerships",
  },
  {
    number: 8,
    name: "8th House",
    sanskrit: "Ayur / Randhra",
    domain: "Longevity, Transformation, Occult, Sudden Events",
  },
  {
    number: 9,
    name: "9th House",
    sanskrit: "Bhagya / Dharma",
    domain: "Fortune, Higher Learning, Father, Spiritual Journey",
  },
  {
    number: 10,
    name: "10th House",
    sanskrit: "Karma / Rajya",
    domain: "Career, Public Status, Leadership, Achievements",
  },
  {
    number: 11,
    name: "11th House",
    sanskrit: "Labha / Aya",
    domain: "Major Gains, Income, Social Network, Aspirations",
  },
  {
    number: 12,
    name: "12th House",
    sanskrit: "Vyaya / Moksha",
    domain: "Spiritual Liberation, Foreign Lands, Subconscious",
  },
];

export default function GrahaSandboxStudio() {
  const [selectedPlanetId, setSelectedPlanetId] = useState<string>("sun");
  const [selectedHouseNumber, setSelectedHouseNumber] = useState<number>(10);

  const planet = useMemo(
    () => PLANETS.find((p) => p.id === selectedPlanetId) || PLANETS[0],
    [selectedPlanetId]
  );
  const house = useMemo(
    () => HOUSES.find((h) => h.number === selectedHouseNumber) || HOUSES[9],
    [selectedHouseNumber]
  );

  const aspectedHouses = useMemo(() => {
    return aspectOffsets(planet.name_en).map((offset) => {
      const target = (house.number + offset - 1) % 12;
      return target === 0 ? 12 : target;
    });
  }, [planet, house]);

  const handleConsult = () => {
    const prompt = `What are the complete Vedic indications and remedies for ${planet.name_en} (${planet.vedic}) placed in the ${house.number}th House (${house.sanskrit} Bhava) in a Janam Kundali?`;
    try {
      safeSessionSet("seo_entry_intent", "chart_report");
      safeSessionSet("seo_entry_path", "/graha");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-8 shadow-sm">
      <StudioHeader />
      <PlanetSelector selectedId={selectedPlanetId} onSelect={setSelectedPlanetId} />
      <HouseSelector selectedNumber={selectedHouseNumber} onSelect={setSelectedHouseNumber} />
      <SynthesisCard
        planet={planet}
        house={house}
        aspectedHouses={aspectedHouses}
        onConsult={handleConsult}
      />
    </div>
  );
}

function StudioHeader() {
  return (
    <div className="text-center max-w-2xl mx-auto mb-6">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        Interactive Kundali Sandbox
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">
        Planetary Placement &amp; House Analyzer
      </h2>
      <p className="text-body-small text-text-secondary mt-1">
        Select any Graha and House to see its Parashari drishti, the bhava class it falls in, and
        what that graha signifies. Dignity depends on the rashi the graha occupies, not on the
        bhava, so it needs your birth details.
      </p>
    </div>
  );
}

function PlanetSelector({
  selectedId,
  onSelect,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="mb-6">
      <label className="block text-caption font-bold uppercase tracking-wider text-text-muted mb-2">
        1. Select Vedic Graha (Planet)
      </label>
      <div className="flex flex-wrap gap-2">
        {PLANETS.map((p) => {
          const isSelected = p.id === selectedId;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p.id)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-body-small font-semibold transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle shadow-sm ring-2 ring-intent-brand/20 text-text-primary"
                  : "border-border-default bg-surface-raised hover:bg-surface-warm text-text-secondary"
              }`}
            >
              <span>{p.symbol}</span>
              <span>{p.name_en}</span>
              <span className="text-caption text-text-muted">({p.vedic})</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HouseSelector({
  selectedNumber,
  onSelect,
}: {
  selectedNumber: number;
  onSelect: (n: number) => void;
}) {
  return (
    <div className="mb-8">
      <label className="block text-caption font-bold uppercase tracking-wider text-text-muted mb-2">
        2. Select Kundali Bhava (House 1–12)
      </label>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {HOUSES.map((h) => {
          const isSelected = h.number === selectedNumber;
          return (
            <button
              key={h.number}
              type="button"
              onClick={() => onSelect(h.number)}
              className={`p-2.5 rounded-xl border text-left transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle shadow-sm ring-2 ring-intent-brand/20"
                  : "border-border-default bg-surface-raised hover:bg-surface-warm"
              }`}
            >
              <span className="block text-body-small font-bold text-text-primary">{h.name}</span>
              <span className="block text-caption text-text-muted truncate">{h.sanskrit}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SynthesisCard({
  planet,
  house,
  aspectedHouses,
  onConsult,
}: {
  planet: PlanetDef;
  house: HouseDef;
  aspectedHouses: number[];
  onConsult: () => void;
}) {
  const chatHref = buildSEOAgentHref(getSEOIntentConfigForSource("/graha"), "/graha", "en");

  // A bhava can hold more than one class — the 1st is both Kendra and Trikona —
  // so the badge shows them all and colours by the strongest signal present.
  const classes = bhavaClasses(house.number);
  const classLabel = classes.join(" · ");
  const badgeColor = classes.some((name) => name.startsWith("Kendra"))
    ? "bg-intent-brand/10 text-intent-brand"
    : classes.some((name) => name.startsWith("Trikona"))
      ? "bg-success/10 text-success"
      : classes.some((name) => name.startsWith("Dusthana"))
        ? "bg-warning/10 text-warning"
        : "bg-surface-raised text-text-secondary";

  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              {planet.symbol} {planet.name_en} in {house.name}
            </h3>
            <span className={`px-3 py-0.5 rounded-full text-caption font-bold ${badgeColor}`}>
              {classLabel}
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            Signifies: <strong className="text-text-primary">{house.domain}</strong>
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          Consult on this Placement ✦
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
        <div>
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted">
            Planetary Nature &amp; Karaka
          </p>
          <p className="text-body-small text-text-primary mt-1 leading-relaxed">
            {planet.name_en} governs {planet.karaka}. Positioned in the {house.number}th House, its
            natural energy activates the themes of {house.sanskrit} Bhava.
          </p>
        </div>

        <div>
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted">
            Special Drishti (Aspects Cast)
          </p>
          <div className="mt-1 flex flex-wrap gap-1.5 items-center">
            {aspectedHouses.map((hNum) => (
              <span
                key={hNum}
                className="px-2.5 py-1 rounded-md text-caption font-bold bg-surface-raised border border-border-default text-text-primary"
              >
                Aspects House {hNum}
              </span>
            ))}
          </div>
          <p className="text-caption text-text-muted mt-2">
            Planets influence not only their seated house, but cast special aspects (Drishti) across
            opposite and trinal bhavas.
          </p>
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-muted">
          Need complete Kundali analysis with exact degrees and divisional charts?
        </span>
        <Link
          href="/free-kundali/"
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1"
        >
          Generate Free Janam Kundali →
        </Link>
      </div>
    </div>
  );
}
